/**
 * API client for the tenant-configurable payment-terms catalog.
 * Accounting owns the endpoints; sales/procurement consume read-only.
 */
import { apiGet, apiPost, apiPut, apiDelete } from '../api/axiosInstance';
import type { PaymentTermLineCreate } from '@/generated/api/models/paymentTermLineCreate';
import type { PaymentTerm, PaymentTermCreate, PaymentTermUpdate } from './types';

const BASE = '/api/v1/accounting/payment-terms';

interface DataResponse<T> {
  data: T;
}

// Document event a staged-term line's clock starts from
// (accounting_payment_term_lines.trigger). The generated model IS that value set —
// re-exported so callers building a line keep the vocabulary from this module and no
// hand-written copy can drift from payment_term_line_create.schema.json.
export type { PaymentTermLineTrigger } from '@/generated/api/models/paymentTermLineTrigger';

/**
 * One installment line when persisting a staged term (L1). `proportion` is a
 * fraction of the document total (0.30 = 30%); proportion-based lines sum to 1.0;
 * `fixed_amount` is a DECIMAL amount in the document currency, never cents.
 *
 * This IS the generated request model — aliased so the local `…Input` vocabulary
 * survives at call sites, and `trigger` / `net_days` are optional here exactly
 * because the schema gives them defaults ('net' / 0).
 */
export type PaymentTermLineInput = PaymentTermLineCreate;

export const paymentTermsApi = {
  list: async (isActive?: boolean): Promise<PaymentTerm[]> => {
    const qs = isActive === undefined ? '' : `?is_active=${isActive}`;
    const res = await apiGet<DataResponse<PaymentTerm[]>>(`${BASE}${qs}`);
    return res.data;
  },

  create: async (data: PaymentTermCreate): Promise<PaymentTerm> => {
    const res = await apiPost<DataResponse<PaymentTerm>>(BASE, data);
    return res.data;
  },

  update: async (id: string, data: PaymentTermUpdate): Promise<PaymentTerm> => {
    const res = await apiPut<DataResponse<PaymentTerm>>(`${BASE}/${id}`, data);
    return res.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiDelete(`${BASE}/${id}`);
  },

  /**
   * Atomically replace a term's installment schedule (staged-payment lines).
   * An empty array clears the schedule. Returns the updated term with lines.
   */
  replaceLines: async (id: string, lines: PaymentTermLineInput[]): Promise<PaymentTerm> => {
    const res = await apiPut<DataResponse<PaymentTerm>>(`${BASE}/${id}/lines`, { lines });
    return res.data;
  },
};
